<template>
  <BaseTemplate app-name="RIS System" page-title="">
    <template #content>
      <div
        class="h-screen flex flex-col bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-100 transition-colors duration-200">
        <!-- Main Content -->
        <div class="flex-1 flex overflow-hidden">
          <!-- Content Area -->
          <div class="flex-1 overflow-hidden flex flex-col">
            <!-- Header -->
            <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 shadow-sm">
              <div class="flex flex-wrap justify-between items-center gap-4">
                <div class="flex items-center gap-3">
                  <h2 class="text-xl font-medium" :style="{ color: 'var(--clinic-font-color)' }">{{ currentView === 'list' ? 'Gerenciar Anamneses' : 'Cadastro de Anamnese' }}</h2>
                  <button v-if="currentView === 'form'"
                    class="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    @click="goToList" aria-label="Voltar para lista">
                    <ArrowLeftIcon class="w-5 h-5" />
                  </button>
                </div>
                <div class="flex items-center gap-2">
                  <button v-if="currentView === 'list'"
                    class="clinic-btn text-white rounded-md transition-colors shadow-sm flex items-center gap-2"
                    @click="openNewAnamnesisForm">
                    <PlusIcon class="w-4 h-4" />
                    <span>Nova Anamnese</span>
                  </button>
                  <button v-if="currentView === 'form'"
                    class="clinic-btn text-white rounded-md transition-colors shadow-sm flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                    @click="saveAnamnesis" :disabled="!isFormValid || isSaving">
                    <template v-if="isSaving">
                      <LoaderIcon class="w-4 h-4 animate-spin" />
                      <span>Salvando...</span>
                    </template>
                    <template v-else>
                      <SaveIcon class="w-4 h-4" />
                      <span>{{ editMode ? 'Atualizar' : 'Salvar' }} Anamnese</span>
                    </template>
                  </button>
                </div>
              </div>
            </div>

            <!-- Anamnesis List View -->
            <div v-if="currentView === 'list'" class="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900">
              <div
                class="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-700 p-4">
                <!-- Search and Filter -->
                <div class="mb-4 flex flex-wrap gap-4 items-center">
                  <!-- Campo de busca -->
                  <div class="flex-1 min-w-[240px]">
                    <div class="relative">
                      <input type="text" v-model="searchQuery" placeholder="Buscar anamneses..." @input="loadAnamneses(1)"
                        class="h-10 w-full px-3 pl-10 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      <SearchIcon class="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    </div>
                  </div>

                  <!-- Categoria -->
                  <div class="relative">
                    <select v-model="filterCategory" @change="loadAnamneses(1)"
                      class="h-10 px-3 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:ring-2 focus:ring-blue-500 appearance-none pr-8">
                      <option value="">Todas as categorias</option>
                      <option value="Geral">Geral</option>
                      <option value="Cardiologia">Cardiologia</option>
                      <option value="Neurologia">Neurologia</option>
                      <option value="Ortopedia">Ortopedia</option>
                      <option value="Ginecologia">Ginecologia</option>
                      <option value="Pediatria">Pediatria</option>
                      <option value="Radiologia">Radiologia</option>
                      <option value="Mamografia">Mamografia</option>
                      <option value="Colonoscopia">Colonoscopia</option>
                      <option value="Broncoscopia">Broncoscopia</option>
                      <option value="Endoscopia">Endoscopia</option>
                    </select>
                    <ChevronDownIcon
                      class="w-4 h-4 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>

                  <!-- Status -->
                  <div class="relative">
                    <select v-model="filterStatus" @change="loadAnamneses(1)"
                      class="h-10 px-3 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:ring-2 focus:ring-blue-500 appearance-none pr-8">
                      <option value="">Todos os status</option>
                      <option value="true">Ativo</option>
                      <option value="false">Inativo</option>
                    </select>
                    <ChevronDownIcon
                      class="w-4 h-4 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>

                  <!-- Botão de reset -->
                  <button @click="resetFilters"
                    class="h-10 px-3 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-blue-500">
                    <RefreshCwIcon class="w-4 h-4 inline-block" />
                  </button>
                </div>

                <!-- Loading Overlay -->
                <div v-if="isLoading" class="py-10 flex justify-center items-center">
                  <LoaderIcon class="w-12 h-12 animate-spin text-gray-500 dark:text-gray-400" />
                </div>

                <!-- Anamnesis Table -->
                <div v-else class="overflow-x-auto">
                  <table class="w-full border-collapse">
                    <thead>
                      <tr class="bg-gray-50 dark:bg-gray-900/50 text-left">
                        <th
                          class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Título
                        </th>
                        <th
                          class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Descrição
                        </th>
                        <th
                          class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Categoria
                        </th>
                        <th
                          class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Perguntas
                        </th>
                        <th
                          class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Status
                        </th>
                        <th
                          class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Criado em
                        </th>
                        <th
                          class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="filteredAnamneses.length === 0">
                        <td colspan="7" class="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                          Nenhuma anamnese encontrada
                        </td>
                      </tr>
                      <tr v-for="anamnesis in filteredAnamneses" :key="anamnesis.id"
                        class="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                        <td class="px-4 py-3">
                          <div class="text-sm font-medium">{{ anamnesis.title }}</div>
                        </td>
                        <td class="px-4 py-3">
                          <div class="text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate">
                            {{ anamnesis.description || 'Sem descrição' }}
                          </div>
                        </td>
                        <td class="px-4 py-3 text-sm">{{ anamnesis.category }}</td>
                        <td class="px-4 py-3 text-sm">{{ anamnesis.questionsCount || 0 }}</td>
                        <td class="px-4 py-3 text-sm">
                          <span class="px-2 py-1 text-xs rounded-full" :class="{
                            'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400': anamnesis.isActive,
                            'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400': !anamnesis.isActive
                          }">
                            {{ anamnesis.isActive ? 'Ativo' : 'Inativo' }}
                          </span>
                        </td>
                        <td class="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {{ formatDate(anamnesis.createdAt) }}
                        </td>
                        <td class="px-4 py-3 text-sm">
                          <div class="flex gap-2">
                            <button @click="editAnamnesis(anamnesis)"
                              class="p-1 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                              title="Editar">
                              <EditIcon class="w-4 h-4" />
                            </button>
                            <button @click="confirmDeleteAnamnesis(anamnesis)"
                              class="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                              title="Excluir">
                              <TrashIcon class="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <!-- Paginação -->
                  <div class="flex flex-col md:flex-row md:items-center md:justify-between my-4 gap-2">
                    <div class="flex items-center gap-2 justify-center">
                      <button :disabled="currentPage === 1" @click="loadAnamneses(currentPage - 1)"
                        class="px-3 py-1 rounded bg-emerald-500 dark:bg-gray-700 text-gray-200 dark:text-gray-200 disabled:opacity-50">Anterior</button>
                      <span class="text-sm text-gray-700 dark:text-gray-200">
                        Página {{ currentPage }} de {{ totalPages }}
                      </span>
                      <button :disabled="currentPage === totalPages || totalPages === 0"
                        @click="loadAnamneses(currentPage + 1)"
                        class="px-3 py-1 rounded bg-emerald-500 dark:bg-gray-700 text-gray-200 dark:text-gray-200 disabled:opacity-50">Próxima</button>
                    </div>
                    <div class="flex items-center gap-2 justify-center">
                      <label for="page-size" class="text-sm text-gray-700 dark:text-gray-200">Itens por página:</label>
                      <select v-model.number="pageSize" @change="loadAnamneses(1)"
                        class="rounded border px-2 py-1 text-sm dark:bg-gray-700 dark:text-white">
                        <option :value="5">5</option>
                        <option :value="10">10</option>
                        <option :value="20">20</option>
                        <option :value="50">50</option>
                      </select>
                      <span class="text-xs text-gray-500 dark:text-gray-400">Total: {{ totalCount }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Anamnesis Form View with Tabs -->
            <div v-if="currentView === 'form'" class="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900">
              <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-700">
                <!-- Tabs Navigation -->
                <div class="border-b border-gray-200 dark:border-gray-700">
                  <div class="flex overflow-x-auto">
                    <button v-for="tab in tabs" :key="tab.id" @click="activeTab = tab.id"
                      class="px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors focus:outline-none"
                      :class="{
                        'border-b-2': activeTab === tab.id,
                        'border-b-2': activeTab === tab.id
                      }"
                      :style="{
                        color: activeTab === tab.id ? 'var(--clinic-icon-color)' : 'var(--clinic-font-color)',
                        borderColor: activeTab === tab.id ? 'var(--clinic-icon-color)' : 'transparent'
                      }">
                      <div class="flex items-center gap-2">
                        <component :is="tab.icon" class="w-4 h-4" />
                        {{ tab.name }}
                      </div>
                    </button>
                  </div>
                </div>

                <!-- Tab Content -->
                <div class="p-6">
                  <!-- Aba de Identificação -->
                  <div v-if="activeTab === 'identification'" class="space-y-6">
                    <div>
                      <h3 class="text-lg font-medium mb-4 text-gray-900 dark:text-white">Informações Básicas</h3>
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div class="md:col-span-2">
                          <label for="title" class="block text-sm font-medium mb-1">Título da Anamnese*</label>
                          <input id="title" type="text" v-model="currentAnamnesis.title" required
                            class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                        </div>

                        <div>
                          <label for="category" class="block text-sm font-medium mb-1">Categoria*</label>
                          <select id="category" v-model="currentAnamnesis.category" required
                            class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8">
                            <option value="">Selecione</option>
                            <option value="Geral">Geral</option>
                            <option value="Cardiologia">Cardiologia</option>
                            <option value="Neurologia">Neurologia</option>
                            <option value="Ortopedia">Ortopedia</option>
                            <option value="Ginecologia">Ginecologia</option>
                            <option value="Pediatria">Pediatria</option>
                            <option value="Radiologia">Radiologia</option>
                          </select>
                        </div>

                        <div>
                          <label for="version" class="block text-sm font-medium mb-1">Versão</label>
                          <input id="version" type="text" v-model="currentAnamnesis.version"
                            class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                        </div>

                        <div class="md:col-span-2">
                          <label for="description" class="block text-sm font-medium mb-1">Descrição</label>
                          <textarea id="description" v-model="currentAnamnesis.description" rows="3"
                            class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"></textarea>
                        </div>
                      </div>
                    </div>

                    <!-- Configurações -->
                    <div>
                      <h3 class="text-lg font-medium mb-4 text-gray-900 dark:text-white">Configurações</h3>
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div class="space-y-3">
                          <div class="flex items-center">
                            <input id="isActive" type="checkbox" v-model="currentAnamnesis.isActive"
                              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                            <label for="isActive" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                              Anamnese ativa
                            </label>
                          </div>

                          <div class="flex items-center">
                            <input id="isMandatory" type="checkbox" v-model="currentAnamnesis.isMandatory"
                              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                            <label for="isMandatory" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                              Preenchimento obrigatório
                            </label>
                          </div>

                          <div class="flex items-center">
                            <input id="allowPartialSave" type="checkbox" v-model="currentAnamnesis.allowPartialSave"
                              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                            <label for="allowPartialSave" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                              Permitir salvamento parcial
                            </label>
                          </div>
                        </div>

                        <div class="space-y-3">
                          <div class="flex items-center">
                            <input id="showInPortal" type="checkbox" v-model="currentAnamnesis.showInPortal"
                              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                            <label for="showInPortal" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                              Exibir no portal do paciente
                            </label>
                          </div>

                          <div class="flex items-center">
                            <input id="requiresSignature" type="checkbox" v-model="currentAnamnesis.requiresSignature"
                              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                            <label for="requiresSignature" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                              Requer assinatura digital
                            </label>
                          </div>

                          <div class="flex items-center">
                            <input id="generatePdf" type="checkbox" v-model="currentAnamnesis.generatePdf"
                              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                            <label for="generatePdf" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                              Gerar PDF automaticamente
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Aba de Perguntas -->
                  <div v-if="activeTab === 'questions'" class="space-y-6">
                    <div class="flex justify-between items-center">
                      <h3 class="text-lg font-medium text-gray-900 dark:text-white">Perguntas da Anamnese</h3>
                      <button @click="addQuestion"
                        class="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors shadow-sm flex items-center gap-2">
                        <PlusIcon class="w-4 h-4" />
                        <span>Adicionar Pergunta</span>
                      </button>
                    </div>

                    <div v-if="currentAnamnesis.questions.length === 0" class="text-center py-8 text-gray-500 dark:text-gray-400">
                      <ClipboardListIcon class="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Nenhuma pergunta adicionada ainda.</p>
                      <p class="text-sm">Clique em "Adicionar Pergunta" para começar.</p>
                    </div>

                    <div v-else class="space-y-4">
                      <div v-for="(question, index) in currentAnamnesis.questions" :key="question.id || index"
                        class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
                        <div class="flex justify-between items-start mb-3">
                          <span class="text-sm font-medium text-gray-500 dark:text-gray-400">Pergunta {{ index + 1 }}</span>
                          <button @click="removeQuestion(index)"
                            class="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
                            <TrashIcon class="w-4 h-4" />
                          </button>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div class="md:col-span-2">
                            <label class="block text-sm font-medium mb-1">Texto da Pergunta*</label>
                            <textarea v-model="question.text" rows="2" required
                              class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"></textarea>
                          </div>

                          <div>
                            <label class="block text-sm font-medium mb-1">Tipo de Resposta*</label>
                            <select v-model="question.type" required
                              class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                              <option value="">Selecione</option>
                              <option value="text">Texto Livre</option>
                              <option value="textarea">Texto Longo</option>
                              <option value="number">Número</option>
                              <option value="date">Data</option>
                              <option value="select">Seleção Única</option>
                              <option value="multiselect">Seleção Múltipla</option>
                              <option value="radio">Opção Única (Radio)</option>
                              <option value="checkbox">Múltipla Escolha</option>
                              <option value="boolean">Sim/Não</option>
                              <option value="image">Imagem com Anotações</option>
                            </select>
                          </div>

                          <div>
                            <label class="block text-sm font-medium mb-1">Ordem</label>
                            <input type="number" v-model.number="question.order" min="1"
                              class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                          </div>

                          <div v-if="['select', 'multiselect', 'radio', 'checkbox'].includes(question.type)" class="md:col-span-2">
                            <label class="block text-sm font-medium mb-1">Opções (uma por linha)</label>
                            <textarea v-model="question.options" rows="3" placeholder="Opção 1&#10;Opção 2&#10;Opção 3"
                              class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"></textarea>
                          </div>

                          <div v-if="question.type === 'image'" class="md:col-span-2 space-y-4">
                            <div>
                              <label class="block text-sm font-medium mb-1">Imagem Base*</label>
                              <div class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                                <div v-if="question.imageUrl" class="space-y-3">
                                  <img :src="question.imageUrl" :alt="question.imageAlt || 'Imagem da pergunta'"
                                    class="max-w-full h-auto max-h-64 mx-auto rounded border" />
                                  <div class="flex justify-center gap-2">
                                    <button type="button" @click="removeQuestionImage(question)"
                                      class="px-3 py-1 text-sm bg-red-500 hover:bg-red-600 text-white rounded">
                                      Remover Imagem
                                    </button>
                                    <button type="button" @click="replaceQuestionImage(question)"
                                      class="px-3 py-1 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded">
                                      Substituir
                                    </button>
                                  </div>
                                </div>
                                <div v-else class="text-center">
                                  <input type="file" :id="`image-upload-${index}`" @change="handleImageUpload($event, question)"
                                    accept="image/*" class="hidden" />
                                  <label :for="`image-upload-${index}`"
                                    class="cursor-pointer inline-flex flex-col items-center justify-center p-6 hover:bg-gray-50 dark:hover:bg-gray-800 rounded">
                                    <svg class="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span class="text-sm text-gray-600 dark:text-gray-400">Clique para fazer upload da imagem</span>
                                    <span class="text-xs text-gray-500 dark:text-gray-500 mt-1">PNG, JPG, GIF até 5MB</span>
                                  </label>
                                </div>
                              </div>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label class="block text-sm font-medium mb-1">Texto alternativo da imagem</label>
                                <input type="text" v-model="question.imageAlt" placeholder="Descrição da imagem para acessibilidade"
                                  class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                              </div>

                              <div>
                                <label class="block text-sm font-medium mb-1">Cor da caneta</label>
                                <select v-model="question.drawingColor"
                                  class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                                  <option value="#ff0000">Vermelho</option>
                                  <option value="#0000ff">Azul</option>
                                  <option value="#00ff00">Verde</option>
                                  <option value="#ffff00">Amarelo</option>
                                  <option value="#ff00ff">Magenta</option>
                                  <option value="#000000">Preto</option>
                                </select>
                              </div>

                              <div>
                                <label class="block text-sm font-medium mb-1">Espessura da linha</label>
                                <select v-model.number="question.lineWidth"
                                  class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                                  <option :value="2">Fina (2px)</option>
                                  <option :value="4">Média (4px)</option>
                                  <option :value="6">Grossa (6px)</option>
                                  <option :value="8">Muito Grossa (8px)</option>
                                </select>
                              </div>

                              <div>
                                <label class="block text-sm font-medium mb-1">Ferramentas permitidas</label>
                                <div class="space-y-2">
                                  <div class="flex items-center">
                                    <input type="checkbox" v-model="question.allowDrawing"
                                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                                    <label class="ml-2 block text-sm text-gray-900 dark:text-gray-300">Desenho livre</label>
                                  </div>
                                  <div class="flex items-center">
                                    <input type="checkbox" v-model="question.allowShapes"
                                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                                    <label class="ml-2 block text-sm text-gray-900 dark:text-gray-300">Formas (círculo, retângulo)</label>
                                  </div>
                                  <div class="flex items-center">
                                    <input type="checkbox" v-model="question.allowText"
                                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                                    <label class="ml-2 block text-sm text-gray-900 dark:text-gray-300">Adicionar texto</label>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div>
                              <label class="block text-sm font-medium mb-1">Instruções específicas para marcação</label>
                              <textarea v-model="question.drawingInstructions" rows="2"
                                placeholder="Ex: Marque com um círculo as áreas de dor ou desconforto..."
                                class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"></textarea>
                            </div>
                          </div>

                          <!-- Preview da funcionalidade de desenho -->
                          <div v-if="question.type === 'image' && question.imageUrl" class="md:col-span-2 mt-4">
                            <ImageAnnotationPreview 
                              :question="question" 
                              @update-question="(updatedQuestion) => Object.assign(question, updatedQuestion)"
                            />
                          </div>

                          <div class="md:col-span-2 flex flex-wrap gap-4">
                            <div class="flex items-center">
                              <input type="checkbox" v-model="question.required"
                                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                              <label class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                                Obrigatória
                              </label>
                            </div>

                            <div class="flex items-center">
                              <input type="checkbox" v-model="question.conditional"
                                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                              <label class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                                Condicional
                              </label>
                            </div>
                          </div>

                          <div v-if="question.conditional" class="md:col-span-2">
                            <label class="block text-sm font-medium mb-1">Condição para exibir</label>
                            <input type="text" v-model="question.condition" placeholder="Ex: pergunta_1 == 'Sim'"
                              class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Aba de Configurações Avançadas -->
                  <div v-if="activeTab === 'settings'" class="space-y-6">
                    <div>
                      <h3 class="text-lg font-medium mb-4 text-gray-900 dark:text-white">Configurações Avançadas</h3>
                      
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <!-- Validações -->
                        <div>
                          <h4 class="text-md font-medium mb-3 text-gray-900 dark:text-white">Validações</h4>
                          <div class="space-y-3">
                            <div>
                              <label for="minAge" class="block text-sm font-medium mb-1">Idade mínima</label>
                              <input id="minAge" type="number" min="0" v-model="currentAnamnesis.minAge"
                                class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                            </div>

                            <div>
                              <label for="maxAge" class="block text-sm font-medium mb-1">Idade máxima</label>
                              <input id="maxAge" type="number" min="0" v-model="currentAnamnesis.maxAge"
                                class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                            </div>

                            <div>
                              <label for="gender" class="block text-sm font-medium mb-1">Gênero</label>
                              <select id="gender" v-model="currentAnamnesis.gender"
                                class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                                <option value="all">Todos</option>
                                <option value="male">Masculino</option>
                                <option value="female">Feminino</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        <!-- Notificações -->
                        <div>
                          <h4 class="text-md font-medium mb-3 text-gray-900 dark:text-white">Notificações</h4>
                          <div class="space-y-3">
                            <div class="flex items-center">
                              <input id="notifyOnComplete" type="checkbox" v-model="currentAnamnesis.notifyOnComplete"
                                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                              <label for="notifyOnComplete" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                                Notificar ao completar
                              </label>
                            </div>

                            <div class="flex items-center">
                              <input id="sendEmailCopy" type="checkbox" v-model="currentAnamnesis.sendEmailCopy"
                                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                              <label for="sendEmailCopy" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                                Enviar cópia por email
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Instruções -->
                    <div>
                      <h4 class="text-md font-medium mb-3 text-gray-900 dark:text-white">Instruções</h4>
                      <div class="space-y-4">
                        <div>
                          <label for="instructions" class="block text-sm font-medium mb-1">Instruções para o paciente</label>
                          <textarea id="instructions" v-model="currentAnamnesis.instructions" rows="3"
                            placeholder="Instruções que aparecerão antes do preenchimento da anamnese..."
                            class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"></textarea>
                        </div>

                        <div>
                          <label for="completionMessage" class="block text-sm font-medium mb-1">Mensagem de conclusão</label>
                          <textarea id="completionMessage" v-model="currentAnamnesis.completionMessage" rows="2"
                            placeholder="Mensagem exibida após completar a anamnese..."
                            class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"></textarea>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Form Actions -->
                  <div class="mt-8 flex justify-between items-center">
                    <div class="flex gap-2">
                      <!-- Navigation buttons could go here -->
                    </div>
                    <div class="flex gap-3">
                      <button
                        class="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md transition-colors"
                        @click="goToList">
                        Cancelar
                      </button>
                      <button
                        class="clinic-btn text-white rounded-md transition-colors shadow-sm disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center gap-2"
                        @click="saveAnamnesis" :disabled="!isFormValid || isSaving">
                        <template v-if="isSaving">
                           <LoaderIcon class="w-4 h-4 animate-spin" />
                           <span>Salvando...</span>
                        </template>
                        <template v-else>
                           <SaveIcon class="w-4 h-4" />
                           <span>{{ editMode ? 'Atualizar' : 'Salvar' }}</span> 
                        </template>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Success Modal -->
            <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              v-if="showSuccessModal" @click.self="closeSuccessModal">
              <div
                class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fadeIn">
                <div class="p-6 text-center">
                  <div
                    class="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400 mx-auto mb-4">
                    <CheckIcon class="w-8 h-8" />
                  </div>
                  <h3 class="text-xl font-medium mb-2">{{ editMode ? 'Anamnese Atualizada' : 'Anamnese Cadastrada' }}</h3>
                  <p class="text-gray-600 dark:text-gray-400 mb-6">
                    {{ editMode ? 'A anamnese foi atualizada com sucesso.' : 'A anamnese foi cadastrada com sucesso no sistema.' }}
                  </p>
                  <div class="flex justify-center gap-3">
                    <button
                      class="clinic-btn text-white rounded-md transition-colors shadow-sm"
                      @click="closeSuccessModal">
                      OK
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Delete Confirmation Modal -->
            <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              v-if="showDeleteModal" @click.self="showDeleteModal = false">
              <div
                class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fadeIn">
                <div class="p-6">
                  <div
                    class="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-400 mx-auto mb-4">
                    <AlertTriangleIcon class="w-8 h-8" />
                  </div>
                  <h3 class="text-xl font-medium mb-2 text-center">Confirmar Exclusão</h3>
                  <p class="text-gray-600 dark:text-gray-400 mb-6 text-center">
                    Tem certeza que deseja excluir a anamnese <span class="font-medium">{{ anamnesisToDelete?.title
                      }}</span>?
                    <br />Esta ação não pode ser desfeita.
                  </p>
                  <div class="flex justify-end gap-3">
                    <button
                      class="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md transition-colors"
                      @click="showDeleteModal = false">
                      Cancelar
                    </button>
                    <button
                      class="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors shadow-sm"
                      @click="deleteAnamnesis">
                      Excluir
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </BaseTemplate>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import BaseTemplate from '../templates/BaseTemplate.vue';
import apiClient from '@/services/authService';
import { useClinicStore } from '@/stores/settings/clinicStore';
import ImageAnnotationPreview from './ImageAnnotationPreview.vue';

import {
  Plus as PlusIcon,
  ChevronDown as ChevronDownIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  Trash as TrashIcon,
  Save as SaveIcon,
  ArrowLeft as ArrowLeftIcon,
  RefreshCw as RefreshCwIcon,
  SearchIcon,
  LoaderIcon,
  AlertTriangle as AlertTriangleIcon,
  FileText as FileTextIcon,
  Settings as SettingsIcon,
  ClipboardList as ClipboardListIcon,
  HelpCircle as HelpCircleIcon
} from 'lucide-vue-next';

// Inicializa o clinicStore (usado para aplicar estilos CSS automaticamente)
useClinicStore();

// Estado de exibição
const currentView = ref('list');
const editMode = ref(false);
const showSuccessModal = ref(false);
const showDeleteModal = ref(false);
const anamnesisToDelete = ref(null);
const searchQuery = ref('');
const filterCategory = ref('');
const filterStatus = ref('');
const activeTab = ref('identification');
const currentPage = ref(1);
const pageSize = ref(10);
const totalCount = ref(0);
const isLoading = ref(false);
const isSaving = ref(false); // <-- NOVO: Estado para controlar o processo de salvar


// Abas
const tabs = [
  { id: 'identification', name: 'Identificação', icon: FileTextIcon },
  { id: 'questions', name: 'Perguntas', icon: HelpCircleIcon },
  { id: 'settings', name: 'Configurações', icon: SettingsIcon }
];

// Modelo vazio para anamnese
const emptyAnamnesis = {
  id: null,
  title: '',
  description: '',
  category: '',
  version: '1.0',
  isActive: true,
  isMandatory: false,
  allowPartialSave: true,
  showInPortal: false,
  requiresSignature: false,
  generatePdf: false,
  minAge: null,
  maxAge: null,
  gender: 'all',
  notifyOnComplete: false,
  sendEmailCopy: false,
  emailTemplate: '',
  instructions: '',
  completionMessage: '',
  questions: [],
  questionsCount:0,
  createdAt: null
};

// Modelo vazio para pergunta
const emptyQuestion = {
  id: null,
  text: '',
  type: '',
  options: '',
  required: false,
  conditional: false,
  condition: '',
  order: 1,
  // Campos específicos para imagens
  imageUrl: '',
  imageAlt: '',
  drawingColor: '#ff0000',
  lineWidth: 4,
  allowDrawing: true,
  allowShapes: true,
  allowText: false,
  drawingInstructions: ''
};

// Dados reativos
const anamneses = ref([]);
const currentAnamnesis = ref({ ...emptyAnamnesis });

// Mapeia resposta da API → UI
function transformApiToUi(api) {
  return {
    id: api.id,
    title: api.titulo,
    description: api.descricao,
    category: api.categoria,
    version: api.versao,
    isActive: api.ativo,
    isMandatory: api.obrigatorio,
    allowPartialSave: api.permite_salvamento_parcial,
    showInPortal: api.exibir_portal,
    requiresSignature: api.requer_assinatura,
    generatePdf: api.gerar_pdf,
    minAge: api.idade_minima,
    maxAge: api.idade_maxima,
    gender: api.genero,
    notifyOnComplete: api.notificar_conclusao,
    sendEmailCopy: api.enviar_email,
    emailTemplate: api.template_email,
    instructions: api.instrucoes,
    completionMessage: api.mensagem_conclusao,
    questions: api.perguntas ? api.perguntas.map(transformQuestionApiToUi) : [],
    questionsCount: api.questions_count || 0,
    createdAt: api.criado_em
  };
}

function transformQuestionApiToUi(api) {
  return {
    id: api.id,
    text: api.texto,
    type: api.tipo,
    options: api.opcoes,
    required: api.obrigatoria,
    conditional: api.condicional,
    condition: api.condicao,
    order: api.ordem,
    imageUrl: api.imagem_url,
    imageAlt: api.imagem_alt,
    drawingColor: api.cor_desenho || '#ff0000',
    lineWidth: api.espessura_linha || 4,
    allowDrawing: api.permite_desenho !== false,
    allowShapes: api.permite_formas !== false,
    allowText: api.permite_texto || false,
    drawingInstructions: api.instrucoes_desenho || ''
  };
}

// Prepara payload UI → API
function transformUiToApi(ui) {
  return {
    titulo: ui.title,
    descricao: ui.description,
    categoria: ui.category,
    versao: ui.version,
    ativo: ui.isActive,
    obrigatorio: ui.isMandatory,
    permite_salvamento_parcial: ui.allowPartialSave,
    exibir_portal: ui.showInPortal,
    requer_assinatura: ui.requiresSignature,
    gerar_pdf: ui.generatePdf,
    idade_minima: ui.minAge,
    idade_maxima: ui.maxAge,
    genero: ui.gender,
    notificar_conclusao: ui.notifyOnComplete,
    enviar_email: ui.sendEmailCopy,
    template_email: ui.emailTemplate,
    instrucoes: ui.instructions,
    mensagem_conclusao: ui.completionMessage,
    perguntas: ui.questions.map(transformQuestionUiToApi)
  };
}

function transformQuestionUiToApi(ui) {
  return {
    texto: ui.text,
    tipo: ui.type,
    opcoes: ui.options,
    obrigatoria: ui.required,
    condicional: ui.conditional,
    condicao: ui.condition,
    ordem: ui.order,
    imagem_url: ui.imageUrl,
    imageAlt: ui.imageAlt,
    cor_desenho: ui.drawingColor,
    espessura_linha: ui.lineWidth,
    permite_desenho: ui.allowDrawing,
    permite_formas: ui.allowShapes,
    permite_texto: ui.allowText,
    instrucoes_desenho: ui.drawingInstructions
  };
}

const totalPages = computed(() => Math.ceil(totalCount.value / pageSize.value));

async function loadAnamneses(page = 1) {
  isLoading.value = true;
  try {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('page_size', pageSize.value);
    if (searchQuery.value) params.append('search', searchQuery.value.trim());
    if (filterCategory.value) params.append('categoria', filterCategory.value);
    if (filterStatus.value) params.append('ativo', filterStatus.value);

    const { data } = await apiClient.get(`/api/v1/anamneses/list/?${params.toString()}`);
    totalCount.value = data.count;
    currentPage.value = page;
    anamneses.value = data.results.map(transformApiToUi);
  } catch (err) {
    console.error('Erro ao carregar anamneses:', err);
  } finally {
    isLoading.value = false;
  }
}


async function saveAnamnesis() {
 
  if (!isFormValid.value || isSaving.value) return;

  isSaving.value = true; // Ativa o estado de "salvando"
  
  try {
    const payload = transformUiToApi(currentAnamnesis.value);
    if (editMode.value) {
      await apiClient.put(`/api/v1/anamneses/update/${currentAnamnesis.value.id}/`, payload);
    } else {
      await apiClient.post('/api/v1/anamneses/add/', payload);
    }
    showSuccessModal.value = true;
    await loadAnamneses(currentPage.value);
  } catch (err) {
    console.error('Erro ao salvar anamnese:', err);
    // Aqui você poderia adicionar um modal/toast de erro
  } finally {
    isSaving.value = false; // Desativa o estado de "salvando", ocorrendo sucesso ou erro
  }
}


async function deleteAnamnesis() {
  if (!anamnesisToDelete.value) return;
  isLoading.value = true;
  try {
    await apiClient.delete(`/api/v1/anamneses/delete/${anamnesisToDelete.value.id}/`);
    showDeleteModal.value = false;
    anamnesisToDelete.value = null;
    await loadAnamneses(currentPage.value);
  } catch (err) {
    console.error('Erro ao excluir anamnese:', err);
  } finally {
    isLoading.value = false;
  }
}

function openNewAnamnesisForm() {
  currentAnamnesis.value = { ...emptyAnamnesis, questions: [] };
  editMode.value = false;
  activeTab.value = 'identification';
  currentView.value = 'form';
}

async function editAnamnesis(anamnesis) {
  isLoading.value = true;
  try {
    const { data } = await apiClient.get(`/api/v1/anamneses/get/${anamnesis.id}/`);
    currentAnamnesis.value = transformApiToUi(data);
    editMode.value = true;
    activeTab.value = 'identification';
    currentView.value = 'form';
  } catch (err) {
    console.error('Erro ao buscar detalhes da anamnese:', err);
  } finally {
    isLoading.value = false;
  }
}

function addQuestion() {
  const newQuestion = { 
    ...emptyQuestion, 
    order: currentAnamnesis.value.questions.length + 1 
  };
  currentAnamnesis.value.questions.push(newQuestion);
}

function removeQuestion(index) {
  currentAnamnesis.value.questions.splice(index, 1);
  // Reordenar as perguntas
  currentAnamnesis.value.questions.forEach((question, idx) => {
    question.order = idx + 1;
  });
}

function goToList() {
  currentView.value = 'list';
}

function confirmDeleteAnamnesis(anamnesis) {
  anamnesisToDelete.value = anamnesis;
  showDeleteModal.value = true;
}

function resetFilters() {
  searchQuery.value = '';
  filterCategory.value = '';
  filterStatus.value = '';
  loadAnamneses(1);
}

function closeSuccessModal() {
  showSuccessModal.value = false;
  currentView.value = 'list';
}

function formatDate(dateString) {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('pt-BR');
}

const filteredAnamneses = computed(() => {
  return anamneses.value;
});

const isFormValid = computed(() => {
  const a = currentAnamnesis.value;
  return a.title.trim() && a.category;
});

// Função para upload de imagem
async function handleImageUpload(event, question) {
  const file = event.target.files[0];
  if (!file) return;

  // Validar tamanho do arquivo (5MB)
  if (file.size > 5 * 1024 * 1024) {
    alert('Arquivo muito grande. Máximo 5MB permitido.');
    return;
  }

  // Validar tipo de arquivo
  if (!file.type.startsWith('image/')) {
    alert('Por favor, selecione apenas arquivos de imagem.');
    return;
  }

  try {
    isLoading.value = true;
    
    // Criar FormData para upload
    const formData = new FormData();
    formData.append('image', file);
    
    // Upload da imagem para o servidor
    const { data } = await apiClient.post('/api/v1/anamneses/upload-image/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    // Atualizar a pergunta com a URL da imagem
    question.imageUrl = data.url;
    question.imageAlt = question.imageAlt || file.name;
    
  } catch (error) {
    console.error('Erro ao fazer upload da imagem:', error);
    alert('Erro ao fazer upload da imagem. Tente novamente.');
  } finally {
    isLoading.value = false;
    // Limpar o input
    event.target.value = '';
  }
}

// Função para remover imagem
function removeQuestionImage(question) {
  question.imageUrl = '';
  question.imageAlt = '';
}

// Função para substituir imagem
function replaceQuestionImage(question) {
  const input = document.getElementById(`image-upload-${currentAnamnesis.value.questions.indexOf(question)}`);
  if (input) {
    input.click();
  }
}

onMounted(async () => {
  isLoading.value = true;
  await loadAnamneses(1);
  isLoading.value = false;
  currentView.value = 'list';
});
</script>

<style>
@import 'tailwindcss/base';
@import 'tailwindcss/components';
@import 'tailwindcss/utilities';

.clinic-btn {
  background-color: var(--clinic-btn-bg);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
  border: none;
  cursor: pointer;
}

.clinic-btn:hover:not(:disabled) {
  background-color: var(--clinic-btn-bg-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.clinic-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.input-font-color {
  color: var(--clinic-font-color);
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@layer base {
  button:hover {
    transform: none !important;
  }
}
</style>
